/**
 * managerOperationsModel — the read-only view-model for Manager Console's operational triage
 * (FULL-0158 / PROD-UX-072).
 *
 * The Manager Console already routes a request to an employee. This module adds the other half of
 * a manager's triage: what the bounded operator projections already say about workload, the human
 * decision queues and recent failures, with each row carrying its owner, its source timestamp and
 * a link to the run (the product's full audit view).
 *
 * NO PRODUCER IS INTRODUCED. Every value comes from four existing `agents_view`-gated reads the
 * product already ships:
 *
 *   - `GET /api/agent-runs/team-queue-summary` — team counters;
 *   - `GET /api/agent-runs/workload-summary` — per-employee counts and the current run;
 *   - `GET /api/agent-runs/pending-approvals` — the human-decision queue;
 *   - `GET /api/agent-runs/recent-failures` — failed runs in the fixed window.
 *
 * The model fetches nothing, decides nothing, approves nothing and grants nothing. Its purpose is
 * to make the navigation target and the wording deterministic and testable:
 *
 *   - an unknown count stays `null` and is rendered as unknown, never as zero;
 *   - a row is "owned" by the Digital Employee the server attributed it to, and a missing
 *     timestamp says so instead of showing a guessed duration;
 *   - failed work is worded `לא הושלם`; the model never produces a "complete" word for it. The
 *     approval decision itself remains on the server, behind the existing `agents_approve` gate.
 */
import { actionTypeLabel } from "./actionTypeVocabulary";
import { runStatusLabel } from "./runStatusVocabulary";
import { formatWaitDuration } from "../pages/AttentionInboxPage";

// The operator sees at most this many rows per queue; the remainder is a count and a link to the
// full surface, never silently dropped.
export const OPERATIONS_ROW_LIMIT = 5;

// agent action risk is the green/amber/red scale (`AnalysisOutput` / `AgentAction.risk_level`),
// not the integer security_policy grade — see `riskGradeVocabulary` for why the two must not share
// a map.
export const ACTION_RISK_LABEL = {
  green: "סיכון נמוך",
  amber: "סיכון בינוני",
  red: "סיכון גבוה",
};

export function actionRiskLabel(value) {
  return Object.prototype.hasOwnProperty.call(ACTION_RISK_LABEL, value)
    ? ACTION_RISK_LABEL[value]
    : null;
}

const text = (value) => (typeof value === "string" && value.trim() ? value.trim() : null);
const finite = (value) => (typeof value === "number" && Number.isFinite(value) ? value : null);

export function timestampLabel(iso) {
  if (!iso) return null;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString("he-IL", { dateStyle: "short", timeStyle: "short" });
}

/** Milliseconds waited since a recorded timestamp; null when the timestamp is absent/unreadable. */
export function waitedMs(iso, now = Date.now()) {
  if (!iso) return null;
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) return null;
  return Math.max(0, now - parsed);
}

/** "זמן ההמתנה אינו ידוע" is a fact about the record, never a fabricated zero-minute wait. */
export function waitAgeLabel(iso, now = Date.now()) {
  const ms = waitedMs(iso, now);
  if (ms == null) return "זמן ההמתנה אינו ידוע";
  return `ממתין ${formatWaitDuration(ms)}`;
}

/** The four counters a manager works from, each already pointing at its evidence surface. */
export function buildQueueCards(team) {
  if (!team) return [];
  return [
    {
      key: "review",
      label: "ממתינים לסקירה",
      value: finite(team.awaiting_review_count),
      to: "/agent-runs?status=pending_review",
      tone: "text-amber-700",
      hint: "החלטה אנושית שמעכבת את הריצה",
    },
    {
      key: "approval",
      label: "ממתינים לאישור",
      value: finite(team.awaiting_approval_count),
      to: "/approval-center",
      tone: "text-orange-700",
      hint: "אישור מתבצע רק במסלול השרת",
    },
    {
      key: "failures",
      label: "ריצות שנכשלו",
      value: finite(team.recent_failure_count),
      to: "/agent-runs?status=failed",
      tone: "text-red-700",
      hint:
        finite(team.recent_failure_window_hours) != null
          ? `ב־${team.recent_failure_window_hours} השעות האחרונות`
          : "בחלון הזמן שנמסר מהשרת",
    },
    {
      key: "active",
      label: "בעבודה כעת",
      value: finite(team.active_runs),
      to: "/agent-runs",
      tone: "text-violet-700",
      hint: "ריצות שנמצאות בעיבוד",
    },
  ];
}

/** The team line under the counters — each value may be independently unknown. */
export function buildTeamLine(team) {
  if (!team) return null;
  return {
    total: finite(team.total_employees),
    available: finite(team.available_count),
    busy: finite(team.busy_count),
  };
}

/**
 * One pending decision, oldest first (the server already sorts the queue; this keeps the order and
 * never re-ranks it). `pending_action` is absent for legacy runs — that row says so and still links
 * to the run instead of inventing a proposal.
 */
export function buildPendingRows(pending, now = Date.now()) {
  const items = Array.isArray(pending?.pending) ? pending.pending : [];
  return items.map((item) => {
    const action = item.pending_action ?? null;
    return {
      key: `${item.agent_id}:${item.run_id}`,
      employeeName: text(item.agent_name) || "עובד לא מזוהה",
      agentId: item.agent_id ?? null,
      runId: item.run_id ?? null,
      runTo: item.run_id ? `/agent-runs/${item.run_id}` : null,
      ticketId: item.ticket_id ?? null,
      ticketTo: item.ticket_id ? `/tickets/${item.ticket_id}` : null,
      ticketLabel:
        item.ticket_number != null ? `קריאה #${item.ticket_number}` : "קריאה מקושרת",
      ticketTitle: text(item.ticket_title),
      statusLabel: runStatusLabel(item.run_status),
      waitStartedAt: item.wait_started_at ?? null,
      waitStartedLabel: timestampLabel(item.wait_started_at),
      waitAge: waitAgeLabel(item.wait_started_at, now),
      actionType: action ? actionTypeLabel(action.action_type) : null,
      actionDescription: action ? text(action.description) : null,
      riskLabel: action ? actionRiskLabel(action.risk_level) : null,
    };
  });
}

/**
 * Per-employee workload ordered for triage: employees with a human-decision queue lead, then
 * employees with recent failures, then active work, then idle, then disabled employees. The server
 * order is alphabetical; this is presentation ordering only and inventing no fact.
 */
export function buildWorkloadRows(workload) {
  const rows = Array.isArray(workload?.agents) ? workload.agents : [];
  const mapped = rows.map((row) => {
    const current = row.current_run ?? null;
    const active = finite(row.active_count);
    return {
      key: row.agent_id,
      agentId: row.agent_id ?? null,
      employeeName: text(row.agent_name) || "עובד לא מזוהה",
      enabled: row.is_enabled !== false,
      active,
      review: finite(row.awaiting_review_count),
      approval: finite(row.awaiting_approval_count),
      failures: finite(row.recent_failure_count),
      hasCurrentRun: Boolean(current?.run_id),
      currentRunTo: current?.run_id ? `/agent-runs/${current.run_id}` : null,
      currentTicketTo: current?.ticket_id ? `/tickets/${current.ticket_id}` : null,
      currentTicketLabel:
        current?.ticket_number != null ? `קריאה #${current.ticket_number}` : "קריאה מקושרת",
      currentTicketTitle: current ? text(current.ticket_title) : null,
      runsTo: row.agent_id ? `/agent-runs?agent_id=${encodeURIComponent(row.agent_id)}` : "/agent-runs",
      reviewTo: row.agent_id
        ? `/agent-runs?agent_id=${encodeURIComponent(row.agent_id)}&status=awaiting_technician_review`
        : "/agent-runs?status=pending_review",
      approvalTo: row.agent_id
        ? `/agent-runs?agent_id=${encodeURIComponent(row.agent_id)}&status=awaiting_approval`
        : "/agent-runs?status=awaiting_approval",
      failuresTo: row.agent_id
        ? `/agent-runs?agent_id=${encodeURIComponent(row.agent_id)}&status=failed`
        : "/agent-runs?status=failed",
    };
  });

  const score = (row) =>
    ((row.review ?? 0) > 0 || (row.approval ?? 0) > 0 ? 4 : 0) +
    ((row.failures ?? 0) > 0 ? 2 : 0) +
    ((row.active ?? 0) > 0 ? 1 : 0);

  return mapped.sort(
    (a, b) =>
      score(b) - score(a) ||
      (a.enabled === b.enabled ? 0 : a.enabled ? -1 : 1) ||
      String(a.employeeName).localeCompare(String(b.employeeName), "he")
  );
}

/**
 * Recent failures (the escalation queue). The row is explicitly `לא הושלם`; a missing failure
 * timestamp reads as unknown, and an absent summary says so rather than implying there was no
 * failure.
 */
export function buildFailureRows(recentFailures) {
  const failures = Array.isArray(recentFailures?.failures) ? recentFailures.failures : [];
  return failures.map((item) => ({
    key: item.run_id,
    employeeName: text(item.agent_name) || "עובד לא מזוהה",
    agentId: item.agent_id ?? null,
    runId: item.run_id ?? null,
    runTo: item.run_id ? `/agent-runs/${item.run_id}` : null,
    ticketId: item.ticket_id ?? null,
    ticketTo: item.ticket_id ? `/tickets/${item.ticket_id}` : null,
    ticketLabel:
      item.ticket_number != null ? `קריאה #${item.ticket_number}` : "קריאה מקושרת",
    ticketTitle: text(item.ticket_title),
    statusLabel: runStatusLabel(item.status),
    completionLabel: "לא הושלם",
    failedAt: item.failed_at ?? null,
    failedAtLabel: timestampLabel(item.failed_at),
    summary: text(item.summary),
  }));
}
